import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { BulkPublishOutcome, Category, ProductListItem } from '../api/types'
import { Banner, Button, Empty, ErrorBanner, Field, Panel, Select, Spinner, TextInput } from '@shop/ui'

/**
 * ما لم يُنشر بعد — <b>وقائمةٌ تُنشر جملةً</b>.
 *
 * <p>
 * ⚠ <b>والنشرُ فعلٌ يخرج إلى العالم</b>، فالشاشةُ تُظهر قبله ما يحتاج الإنسانُ
 * معرفتَه: كم قياسًا لكلّ صنف، وكم منها <b>له سعرٌ سارٍ</b>. وصنفٌ بلا سعرٍ
 * يُنشَر ولا يظهر — وهي أسوأُ حالة: لا هو على الموقع، ولا هو في قائمة ما
 * ينتظر النشر. ولذلك يُعلَّم هنا قبل الضغط لا بعده.
 * </p>
 *
 * <p>
 * ⚠ <b>ونتيجةُ كلِّ صنفٍ تُعرض على حدة.</b> «نُشر ١٠٠» سطرٌ يطمئن ولا يُخبر:
 * قد يكون نصفُها بلا سعرٍ فلم يظهر منه شيء، ولا يُكتشف ذلك إلا من الموقع.
 * </p>
 */
export default function Publishing() {
  const { can } = useAuth()
  const queries = useQueryClient()

  const [term, setTerm] = useState('')
  const [section, setSection] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [outcomes, setOutcomes] = useState<BulkPublishOutcome[] | null>(null)

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories'),
    staleTime: 5 * 60_000,
  })

  const rows = useQuery({
    queryKey: ['unpublished', term, section],
    queryFn: () => api<ProductListItem[]>(
      '/api/v1/products?published=false&limit=200'
      + (term.trim() === '' ? '' : `&q=${encodeURIComponent(term.trim())}`)
      + (section === '' ? '' : `&categoryId=${section}`)),
    placeholderData: keepPreviousData,
    enabled: can(Perm.productView),
  })

  const list = useMemo(() => rows.data ?? [], [rows.data])

  // ⚠ **ولا يُحدَّد ما لا يصلح.** «حدّد الكلَّ» الذي يشمل أصنافًا بلا سعرٍ
  //   يُنتج نشرًا نصفُه بلا أثر، ثمّ بحثًا عن السبب.
  const ready = useMemo(() => list.filter((p) => p.publishableVariants > 0), [list])
  const blocked = list.length - ready.length

  const toggle = (id: string) => {
    setPicked((current) => {
      const next = new Set(current)

      if (!next.delete(id)) {
        next.add(id)
      }

      return next
    })
  }

  const publish = useMutation({
    mutationFn: (productIds: string[]) => api<BulkPublishOutcome[]>('/api/v1/products/publishing', {
      method: 'POST',
      body: { productIds, showOnWebsite: true },
    }),
    onSuccess: (result) => {
      setOutcomes(result)
      setPicked(new Set())
      void queries.invalidateQueries({ queryKey: ['unpublished'] })
      void queries.invalidateQueries({ queryKey: ['products'] })
    },
  })

  if (!can(Perm.productPublish)) {
    return (
      <Panel title="النشرُ على الموقع">
        <Banner tone="warn">
          النشرُ يحتاج صلاحيّةَ «نشرُ منتجٍ على الموقع». اطلبها من المالك.
        </Banner>
      </Panel>
    )
  }

  const chosen = [...picked]

  return (
    <Panel title="أصنافٌ لم تُنشر بعد">
      <Banner tone="info">
        <b>الصنفُ يظهر على الموقع حين يكون له قياسٌ منشورٌ بسعرٍ سارٍ</b> — ونشرُ
        الصنف وحدَه لا يكفي. والنشرُ هنا يُخرج الصنفَ وقياساتِه المُسعَّرةَ معًا.
      </Banner>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field label="بحث" hint="امسح باركودًا — أو اكتب اسمًا أو طرازًا أو رمزًا">
          <TextInput value={term} onChange={setTerm} placeholder="باركود · اسمٌ · طرازٌ · رمز…" />
        </Field>

        <Field label="القسم">
          <Select value={section} onChange={setSection}>
            <option value="">كلُّ الأقسام</option>
            {(categories.data ?? []).filter((c) => c.isActive).map((category) => (
              <option key={category.id} value={category.id}>{category.nameAr}</option>
            ))}
          </Select>
        </Field>
      </div>

      <ErrorBanner error={rows.error ?? publish.error} />

      {outcomes && (
        <div className="mt-4 rounded-md border border-line bg-surface p-3">
          <h3 className="mb-2 text-sm font-bold">
            نتيجةُ النشر — {outcomes.filter((o) => o.shownVariants > 0).length} ظهرت
            {outcomes.some((o) => o.shownVariants === 0)
              && ` · ${outcomes.filter((o) => o.shownVariants === 0).length} لم تظهر`}
          </h3>

          <ul className="grid gap-1 text-sm">
            {outcomes.map((outcome) => (
              <li key={outcome.productId} className="flex flex-wrap items-center gap-2">
                <span className={outcome.shownVariants > 0 ? 'text-ok' : 'text-warn'}>
                  {outcome.shownVariants > 0 ? '✓' : '!'}
                </span>

                <span>{outcome.nameAr ?? outcome.productId}</span>

                {outcome.shownVariants > 0 && (
                  <span className="tabular text-xs text-ink-soft" dir="ltr">
                    {outcome.shownVariants}
                  </span>
                )}

                {outcome.note && <span className="text-xs text-warn">{outcome.note}</span>}
              </li>
            ))}
          </ul>

          <button
            className="mt-2 text-xs text-brand underline"
            type="button"
            onClick={() => setOutcomes(null)}
          >
            أخفِ النتيجة
          </button>
        </div>
      )}

      {rows.isPending ? (
        <div className="mt-4"><Spinner label="تُقرأ الأصناف…" /></div>
      ) : list.length === 0 ? (
        <div className="mt-4">
          <Empty>لا صنفَ ينتظر النشر — كلُّ ما يطابق البحثَ منشورٌ بالفعل.</Empty>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2 border-b border-line pb-3">
            <Button
              disabled={ready.length === 0 || publish.isPending}
              onClick={() => setPicked(new Set(ready.map((p) => p.id)))}
            >
              حدّد الجاهزَ للنشر ({ready.length})
            </Button>

            <Button disabled={picked.size === 0 || publish.isPending} onClick={() => setPicked(new Set())}>
              ألغِ التحديد
            </Button>

            <Button
              tone="primary"
              disabled={chosen.length === 0 || publish.isPending}
              onClick={() => publish.mutate(chosen)}
            >
              {publish.isPending ? 'يُنشر…' : `انشر المحدَّد (${chosen.length})`}
            </Button>

            {blocked > 0 && (
              <span className="text-xs text-warn">
                {blocked} صنفًا بلا سعرٍ سارٍ — نشرُه لن يُظهره.
              </span>
            )}
          </div>

          <ul className="mt-2 grid gap-1">
            {list.map((product) => {
              const canShow = product.publishableVariants > 0

              return (
                <li key={product.id}>
                  <label
                    className={
                      'flex cursor-pointer flex-wrap items-center gap-3 rounded-md border px-3 py-2 '
                      + (picked.has(product.id) ? 'border-brand bg-brand/5' : 'border-line')
                    }
                  >
                    <input
                      type="checkbox"
                      className="size-5"
                      checked={picked.has(product.id)}
                      disabled={!canShow || publish.isPending}
                      onChange={() => toggle(product.id)}
                    />

                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{product.nameAr}</span>
                      <span className="block text-xs text-ink-soft">
                        <span className="tabular" dir="ltr">{product.code}</span>
                        {product.categoryName && ` · ${product.categoryName}`}
                        {` · ${product.variantCount} قياس`}
                      </span>
                    </span>

                    {canShow ? (
                      <span className="text-xs text-ok">
                        {product.publishableVariants} مُسعَّر
                      </span>
                    ) : (
                      // ⚠ **ويُقال السببُ لا «غيرُ جاهز»**: من يقرأ «بلا سعر»
                      //   يعرف ما يفعل، ومن يقرأ «غيرُ جاهز» يسأل.
                      <span className="text-xs text-warn">بلا سعرٍ سارٍ</span>
                    )}

                    <Link
                      className="text-xs text-brand hover:underline"
                      to={`/products/${product.id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      افتح
                    </Link>
                  </label>
                </li>
              )
            })}
          </ul>

          {list.length === 200 && (
            <p className="mt-2 text-xs text-ink-soft">
              تُعرض ٢٠٠ صنفٍ في المرّة — انشر هذه الدفعةَ ثمّ أعِد التحميل لما بعدها.
            </p>
          )}
        </>
      )}
    </Panel>
  )
}
